import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Doughnut, Pie, Line, Bar } from "react-chartjs-2";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import Header from "../../Common/Layout/Header";
import MarketingTabs from "../AllTabs/MarketingTabs";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { RiCalendarLine } from "react-icons/ri";
import moment from "moment";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const LeadManagementDashboard = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dashboardData, setDashboardData] = useState({
    sourceCounts: [],
    leadStatusCounts: [],
    totalLeads: 0,
  });
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Custom input component for date picker (same as in your other components)
  const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
    <div className="input-group">
      <input
        type="text"
        className="form-control"
        value={value}
        onClick={onClick}
        ref={ref}
        readOnly
        placeholder="Select date"
      />
      <button className="btn btn-secondary" type="button" onClick={onClick}>
        <RiCalendarLine />
      </button>
    </div>
  ));

  const formatDate = (date) => {
    return moment(date).format("YYYY-MM-DD");
  };

  // Set default dates to current month on component mount
  useEffect(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0
    );

    setFromDate(firstDayOfMonth);
    setToDate(lastDayOfMonth);
  }, []);

  // Fetch lead statuses
  const GetLeadStatus = useCallback(async () => {
    if (!user?.cid) return;

    try {
      const res = await apiService.getAll(
        `${config.GETLEADSTATUSBYCOMPANY}/${user?.cid}`
      );
      setLeadStatuses(res.data || []);
    } catch (error) {
      console.error("Error fetching lead statuses:", error);
      setLeadStatuses([]);
    }
  }, [user]);

  // Fetch data when component loads or filters change
  useEffect(() => {
    if (user?.cid) {
      GetLeadStatus();
    }
  }, [user, GetLeadStatus]);

  // Fetch data when component loads or filters change
  useEffect(() => {
    if (fromDate && toDate && user?.cid) {
      GetData();
    }
  }, [fromDate, toDate, sourceFilter, statusFilter, user]);

  // Fetch data from backend
  const GetData = useCallback(async () => {
    if (!user?.cid) return;

    setIsLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();

      // Convert Date objects to string format for API
      const fromDateStr =
        fromDate instanceof Date ? formatDate(fromDate) : fromDate;
      const toDateStr = toDate instanceof Date ? formatDate(toDate) : toDate;

      if (fromDateStr) params.append("fromDate", fromDateStr);
      if (toDateStr) params.append("toDate", toDateStr);
      if (sourceFilter !== "all") params.append("sourceName", sourceFilter);
      if (statusFilter !== "all") params.append("leadStatusId", statusFilter);

      // Use the correct API endpoint format
      const url = `${config.GETLEADDASHBOARD}?companyId=${user.cid}&${params}`;
      const response = await apiService.getAll(url);
      setDashboardData(
        response || { sourceCounts: [], leadStatusCounts: [], totalLeads: 0 }
      );
    } catch (error) {
      console.error("Error fetching lead dashboard data:", error);
      setDashboardData({
        sourceCounts: [],
        leadStatusCounts: [],
        totalLeads: 0,
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, fromDate, toDate, sourceFilter, statusFilter]);

  // Reset filters
  const resetFilters = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0
    );

    setFromDate(firstDayOfMonth);
    setToDate(lastDayOfMonth);
    setSourceFilter("all");
    setStatusFilter("all");
  };

  // Handle date changes for DatePicker
  const handleFromDateChange = (date) => {
    setFromDate(date);
  };

  const handleToDateChange = (date) => {
    setToDate(date);
  };

  const statusColors = {
    NEW: {
      background: "linear-gradient(135deg, #4361ee, #3a56d4)",
      chart: "#4361ee",
      icon: "#ffffff",
    },
    "NOT RESPONDING": {
      background: "linear-gradient(135deg, #ef476f, #e03c5f)",
      chart: "#ef476f",
      icon: "#ffffff",
    },
    "FOLLOW UP": {
      background: "linear-gradient(135deg, #f9c74f, #f4b400)",
      chart: "#f9c74f",
      icon: "#ffffff",
    },
    CLOSED: {
      background: "linear-gradient(135deg, #6a4c93, #5a3f80)",
      chart: "#6a4c93",
      icon: "#ffffff",
    },
    REPLIED: {
      background: "linear-gradient(135deg, #10b981, #0da271)",
      chart: "#10b981",
      icon: "#ffffff",
    },
    "NOT WORKING": {
      background: "linear-gradient(135deg, #6c757d, #5a6268)",
      chart: "#6c757d",
      icon: "#ffffff",
    },
  };

  const sourceColors = [
    "#4361ee",
    "#e6683c",
    "#3b5998",
    "#0077b5",
    "#db4a39",
    "#6a4c93",
    "#38b000",
    "#6c757d",
    "#f9c74f",
    "#ef476f",
    "#8b5cf6",
    "#f97316",
    "#10b981",
    "#ec4899",
    "#06b6d4",
  ];

  // Prepare chart data from API response
  const sourceChartData = {
    labels: dashboardData.sourceCounts?.map((item) => item.sourceName) || [],
    datasets: [
      {
        data: dashboardData.sourceCounts?.map((item) => item.count) || [],
        backgroundColor: sourceColors.slice(
          0,
          dashboardData.sourceCounts?.length || 0
        ),
        borderColor: "#fff",
        borderWidth: 2,
      },
    ],
  };

  const statusChartData = {
    labels:
      dashboardData.leadStatusCounts?.map((item) => item.leadStatusName) || [],
    datasets: [
      {
        data: dashboardData.leadStatusCounts?.map((item) => item.count) || [],
        backgroundColor:
          dashboardData.leadStatusCounts?.map(
            (item) => statusColors[item.leadStatusName]?.chart || "#6c757d"
          ) || [],
        borderColor: "#fff",
        borderWidth: 2,
      },
    ],
  };

  const sourceChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
    },
  };

  const statusChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
    },
  };

  const trendChartData = {
    labels: [
      "Sep 12",
      "Sep 15",
      "Sep 18",
      "Sep 21",
      "Sep 24",
      "Sep 27",
      "Sep 30",
      "Oct 3",
      "Oct 6",
      "Oct 9",
      "Oct 12",
    ],
    datasets: [
      {
        label: "Leads",
        data: [32, 28, 45, 36, 43, 29, 38, 41, 47, 35, 42],
        borderColor: "#4361ee",
        tension: 0.3,
        fill: true,
        backgroundColor: "rgba(67, 97, 238, 0.05)",
      },
    ],
  };

  const trendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const conversionChartData = {
    labels: ["B2C", "Instagram", "Facebook", "LinkedIn", "Google"],
    datasets: [
      {
        label: "Conversion Rate %",
        data: [22, 28, 19, 31, 26],
        backgroundColor: "#4895ef",
      },
    ],
  };

  const conversionChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 40,
        ticks: {
          callback: function (value) {
            return value + "%";
          },
        },
      },
    },
  };

  // SVG Icons to replace Font Awesome
  const PlusCircleIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="12" y1="8" x2="12" y2="16"></line>
      <line x1="8" y1="12" x2="16" y2="12"></line>
    </svg>
  );

  const CheckCircleIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
      <polyline points="22 4 12 14.01 9 11.01"></polyline>
    </svg>
  );

  const TimesCircleIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="15" y1="9" x2="9" y2="15"></line>
      <line x1="9" y1="9" x2="15" y2="15"></line>
    </svg>
  );

  const SyncIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M23 4v6h-6"></path>
      <path d="M1 20v-6h6"></path>
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
      <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
    </svg>
  );

  const LockIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
    </svg>
  );

  const BanIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
    </svg>
  );

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
    return null;
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2">
                  <MarketingTabs pageName="LEADDASHBOARD" />
                </div>
                <div className="card-body">
                  {/* Filter Section */}
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="card bg-light">
                        <div className="card-body p-3">
                          <div className="row g-3 align-items-end">
                            {/* Date Range */}
                            <div className="col-md-4">
                              <label className="form-label fw-semibold">
                                Date Range
                              </label>
                              <div className="row g-2">
                                <div className="col-6">
                                  <label className="form-label small">
                                    From Date
                                  </label>
                                  <DatePicker
                                    selected={fromDate}
                                    onChange={handleFromDateChange}
                                    customInput={<CustomDateInput />}
                                    dateFormat="dd-MMM-yyyy"
                                    showYearDropdown
                                    showMonthDropdown
                                    dropdownMode="select"
                                    yearDropdownItemNumber={50}
                                    scrollableYearDropdown
                                    className="form-control"
                                  />
                                </div>
                                <div className="col-6">
                                  <label className="form-label small">
                                    To Date
                                  </label>
                                  <DatePicker
                                    selected={toDate}
                                    onChange={handleToDateChange}
                                    customInput={<CustomDateInput />}
                                    dateFormat="dd-MMM-yyyy"
                                    showYearDropdown
                                    showMonthDropdown
                                    dropdownMode="select"
                                    yearDropdownItemNumber={50}
                                    scrollableYearDropdown
                                    className="form-control"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Source Filter */}
                            <div className="col-md-2">
                              <label
                                htmlFor="sourceFilter"
                                className="form-label fw-semibold"
                              >
                                Source
                              </label>
                              <select
                                className="form-select form-select-sm"
                                id="sourceFilter"
                                value={sourceFilter}
                                onChange={(e) =>
                                  setSourceFilter(e.target.value)
                                }
                              >
                                <option value="all">All Sources</option>
                                <option value="B2C">B2C</option>
                                <option value="Walk In">Walk In</option>
                                <option value="ChatBot">ChatBot</option>
                                <option value="Phone Call">Phone Call</option>
                                <option value="General">General</option>
                              </select>
                            </div>

                            {/* Status Filter - Updated to use dynamic data */}
                            <div className="col-md-2">
                              <label
                                htmlFor="statusFilter"
                                className="form-label fw-semibold"
                              >
                                Status
                              </label>
                              <select
                                className="form-select form-select-sm"
                                id="statusFilter"
                                value={statusFilter}
                                onChange={(e) =>
                                  setStatusFilter(e.target.value)
                                }
                              >
                                <option value="all">All Statuses</option>
                                {leadStatuses.map((status) => (
                                  <option
                                    key={status.leadStatusId}
                                    value={status.leadStatusId}
                                  >
                                    {status.leadStatusName}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Buttons */}
                            <div className="col-md-2">
                              <div className="d-flex gap-2 mt-4">
                                <button
                                  className="btn btn-primary btn-sm w-100"
                                  onClick={GetData}
                                  disabled={isLoading}
                                >
                                  {isLoading ? "Loading..." : "Apply"}
                                </button>
                                <button
                                  className="btn btn-danger btn-sm w-100"
                                  onClick={resetFilters}
                                >
                                  Reset
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Status Cards */}
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="card bg-white">
                        <div className="card-body  bg-light">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0 text-dark">
                              Overview
                            </h5>
                            <span className="badge bg-primary fs-6">
                              Total Leads: {dashboardData.totalLeads}
                            </span>
                          </div>
                          {isLoading ? (
                            <div className="text-center py-4">
                              Loading status data...
                            </div>
                          ) : dashboardData.leadStatusCounts?.length > 0 ? (
                            <div className="row">
                              {dashboardData.leadStatusCounts.map(
                                (status, index) => {
                                  const statusConfig =
                                    statusColors[status.leadStatusName] ||
                                    statusColors["NEW"];
                                  let icon;

                                  switch (status.leadStatusName) {
                                    case "NEW":
                                      icon = <PlusCircleIcon />;
                                      break;
                                    case "NOT RESPONDING":
                                      icon = <TimesCircleIcon />;
                                      break;
                                    case "FOLLOW UP":
                                      icon = <SyncIcon />;
                                      break;
                                    case "CLOSED":
                                      icon = <LockIcon />;
                                      break;
                                    case "REPLIED":
                                      icon = <CheckCircleIcon />;
                                      break;
                                    case "NOT WORKING":
                                      icon = <BanIcon />;
                                      break;
                                    default:
                                      icon = <PlusCircleIcon />;
                                  }

                                  return (
                                    <div
                                      key={index}
                                      className="col-md-4 col-lg-2 mb-3"
                                    >
                                      <div
                                        className="card text-white border-0 shadow-sm h-100"
                                        style={{
                                          background: statusConfig.background,
                                          borderRadius: "15px",
                                          overflow: "hidden",
                                          transition:
                                            "transform 0.3s ease, box-shadow 0.3s ease",
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.transform =
                                            "translateY(-8px) scale(1.03)";
                                          e.currentTarget.style.boxShadow =
                                            "0 8px 20px rgba(0,0,0,0.3)";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.transform =
                                            "translateY(0) scale(1)";
                                          e.currentTarget.style.boxShadow =
                                            "0 4px 12px rgba(0,0,0,0.2)";
                                        }}
                                      >
                                        <div className="card-body d-flex justify-content-between align-items-center p-3">
                                          {/* Left Side */}
                                          <div className="text-start">
                                            <p className="mb-1 fw-semibold text-uppercase small text-white">
                                              {status.leadStatusName}
                                            </p>
                                            <h2
                                              className="mb-0 fw-bold text-white"
                                              style={{ fontSize: "2.5rem" }}
                                            >
                                              {status.count}
                                            </h2>
                                          </div>

                                          {/* Right Side Icon */}
                                          <div
                                            className="status-icon fs-1"
                                            style={{
                                              opacity: 0.9,
                                              transform: "scale(1)",
                                              transition: "transform 0.4s ease",
                                            }}
                                          >
                                            {icon}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-4">
                              No status data available
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Charts */}
                  <div className="row mb-4">
                    {/* Leads by Source */}
                    <div className="col-md-6 mb-3">
                      <div
                        className="card h-100 bg-white border-0 shadow-sm"
                        style={{
                          borderRadius: "15px",
                          transition:
                            "transform 0.3s ease, box-shadow 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform =
                            "translateY(-8px) scale(1.03)";
                          e.currentTarget.style.boxShadow =
                            "0 10px 25px rgba(0,0,0,0.25)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform =
                            "translateY(0) scale(1)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 12px rgba(0,0,0,0.15)";
                        }}
                      >
                        <div className="card-body bg-light">
                          <h5 className="card-title text-dark">
                            Leads by Source
                          </h5>
                          <div style={{ height: "300px" }}>
                            {isLoading ? (
                              <div className="d-flex justify-content-center align-items-center h-100">
                                Loading...
                              </div>
                            ) : dashboardData.sourceCounts?.length > 0 ? (
                              <Doughnut
                                data={sourceChartData}
                                options={sourceChartOptions}
                              />
                            ) : (
                              <div className="d-flex justify-content-center align-items-center h-100">
                                No data available
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Leads by Status */}
                    <div className="col-md-6 mb-3">
                      <div
                        className="card h-100 bg-white border-0 shadow-sm"
                        style={{
                          borderRadius: "15px",
                          transition:
                            "transform 0.3s ease, box-shadow 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform =
                            "translateY(-8px) scale(1.03)";
                          e.currentTarget.style.boxShadow =
                            "0 10px 25px rgba(0,0,0,0.25)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform =
                            "translateY(0) scale(1)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 12px rgba(0,0,0,0.15)";
                        }}
                      >
                        <div className="card-body bg-light">
                          <h5 className="card-title text-dark">
                            Leads by Status
                          </h5>
                          <div style={{ height: "300px" }}>
                            {isLoading ? (
                              <div className="d-flex justify-content-center align-items-center h-100">
                                Loading...
                              </div>
                            ) : dashboardData.leadStatusCounts?.length > 0 ? (
                              <Pie
                                data={statusChartData}
                                options={statusChartOptions}
                              />
                            ) : (
                              <div className="d-flex justify-content-center align-items-center h-100">
                                No data available
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    {/* Leads Trend */}
                    <div className="col-md-8 mb-3">
                      <div
                        className="card h-100 bg-white border-0 shadow-sm"
                        style={{
                          borderRadius: "15px",
                          transition:
                            "transform 0.3s ease, box-shadow 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform =
                            "translateY(-8px) scale(1.03)";
                          e.currentTarget.style.boxShadow =
                            "0 10px 25px rgba(0,0,0,0.25)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform =
                            "translateY(0) scale(1)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 12px rgba(0,0,0,0.15)";
                        }}
                      >
                        <div className="card-body bg-light">
                          <h5 className="card-title text-dark">
                            Leads Trend (Last 30 Days)
                          </h5>
                          <div style={{ height: "300px" }}>
                            <Line
                              data={trendChartData}
                              options={trendChartOptions}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Conversion by Source */}
                    <div className="col-md-4 mb-3">
                      <div
                        className="card h-100 bg-white border-0 shadow-sm"
                        style={{
                          borderRadius: "15px",
                          transition:
                            "transform 0.3s ease, box-shadow 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform =
                            "translateY(-8px) scale(1.03)";
                          e.currentTarget.style.boxShadow =
                            "0 10px 25px rgba(0,0,0,0.25)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform =
                            "translateY(0) scale(1)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 12px rgba(0,0,0,0.15)";
                        }}
                      >
                        <div className="card-body bg-light">
                          <h5 className="card-title text-dark">
                            Conversion by Source
                          </h5>
                          <div style={{ height: "300px" }}>
                            <Bar
                              data={conversionChartData}
                              options={conversionChartOptions}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadManagementDashboard;
